import { User } from '@/features/iam/users/User'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { Model, type ModelProps } from '@/infra/model'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { Job } from '@/features/jobs/Job'
import { once } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'

export type ScopeChangeRequestStatus =
  | 'PendingFinalization'
  | 'PendingAdminApproval'
  | 'PendingClientApproval'
  | 'Accepted'
  | 'Rejected'
  | 'ApplyFailed'
  | 'Unspecified'

export type ResponderType = 'Client' | 'Admin' | 'System'

export type ResponseType = 'Approved' | 'Rejected'

/**
 * A response to a scope change request.
 */
export interface ScopeChangeRequestResponse {
  createdByUser: User
  responderType: ResponderType
  responseType: ResponseType
  reason: string | null
  dateCreated: Date
}

export type ScopeChangeRequestItemType = 'Inventory' | 'Custom'

export type ScopeChangeRequestItemProps = ModelProps<ScopeChangeRequestItem>

export class ScopeChangeRequestItem extends Model<ScopeChangeRequestItem> {
  id = Id.none

  @observable accessor name = ''
  @observable accessor inventoryItemVersionId = Id.none
  @observable accessor quantity = 0
  @observable accessor price = 0
  @observable accessor proPrice = 0
  @observable accessor type: ScopeChangeRequestItemType = 'Custom'
}

export interface ScopeChangeRequestProps extends Omit<
  ModelProps<ScopeChangeRequest>,
  'items'
> {
  items?: ScopeChangeRequestItemProps[]
}

export class ScopeChangeRequest extends Model<ScopeChangeRequest> {
  id = Id.none

  @observable accessor createTime = unixEpoch()
  @observable accessor job = Job.none
  @observable accessor createdBy = User.none
  @observable accessor status: ScopeChangeRequestStatus = 'PendingAdminApproval'

  @observable accessor requireClientApproval = false

  @observable accessor workspace = Workspace.none
  @observable accessor responders: ScopeChangeRequestResponse[] = []
  @observable accessor reason = ''

  readonly items = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new ScopeChangeRequestItem(),
  })

  @computed
  get isCreatedByAdmin() {
    return (
      this.job.collaborators.find(
        (c) => c.member.user.publicId === this.createdBy.publicId,
      ) === undefined
    )
  }

  @computed
  get customItems() {
    return this.items.filter((i) => i.type === 'Custom')
  }

  /**
   * Whether the request has custom items or not
   */
  @computed
  get hasCustomItems() {
    return this.customItems.length > 0
  }

  @computed
  get inventoryItems() {
    return this.items.filter((i) => i.type === 'Inventory')
  }

  /**
   * Whether the request has inventory items or not
   */
  @computed
  get hasInventoryItems() {
    return this.inventoryItems.length > 0
  }

  @computed
  get isPendingFinalization() {
    return this.status === 'PendingFinalization'
  }

  @computed
  get isPendingAdminApproval() {
    return this.status === 'PendingAdminApproval'
  }

  @computed
  get isPendingClientApproval() {
    return this.status === 'PendingClientApproval'
  }

  @computed
  get isAccepted() {
    return this.status === 'Accepted'
  }

  @computed
  get isRejected() {
    return this.status === 'Rejected'
  }

  @computed
  get price() {
    return this.items
      .map((i) => i.price)
      .reduce((total, price) => total + price, 0)
  }

  /**
   * Whether or not the scope change request is pending
   */
  @computed
  get isPending() {
    return ['PendingClientApproval', 'PendingAdminApproval'].includes(
      this.status,
    )
  }

  /**
   * Whether or not the scope change request is resolved
   */
  @computed
  get isResolved() {
    return ['Accepted', 'Rejected'].includes(this.status)
  }

  @computed
  get jobClient() {
    return this.job.collaborators.find((c) => c.type === 'Client')
  }

  /**
   * Whether the request has responders or not
   */
  @computed
  get hasResponders() {
    return this.responders.length > 0
  }

  @computed
  get lastResponder() {
    // Responders are ordered most-recent-first by the backend, so take the first entry.
    return this.responders[0]
  }

  /**
   * The collaborator user type of the member which created the job scope change request.
   */
  @computed
  get reasonCollaboratorType() {
    const reasonCollaborator = this.job.collaborators.find(
      (c) => c.member.user.publicId === this.createdBy.publicId,
    )

    // If collaborator is not on the job, it was an admin.
    if (reasonCollaborator === undefined) {
      return 'Admin'
    }

    return reasonCollaborator.type === 'Client' ? 'Client' : 'Provider'
  }

  static get none() {
    return lazyNone()
  }

  @action.bound
  override set({ items, ...props }: Partial<ScopeChangeRequestProps>) {
    if (items) {
      this.items.reset(items)
    }

    return super.set(props)
  }
}

/**
 * Initial values of a request's price breakdown.
 */
export interface ScopeChangeRequestPriceBreakDownProps {
  id: Id
  previousTotalPrice: number
  newTotalPrice: number
  previousTotalProPrice: number
  newTotalProPrice: number
  status: ScopeChangeRequestStatus
  items: ScopeChangePriceBreakDownItemForAdmin[]
}

/**
 * A request's price breakdown: its totals and each item's change.
 */
export class ScopeChangeRequestPriceBreakDown {
  readonly id: Id
  readonly previousTotalPrice: number
  readonly newTotalPrice: number
  readonly previousTotalProPrice: number
  readonly newTotalProPrice: number
  readonly status: ScopeChangeRequestStatus
  readonly items: ScopeChangePriceBreakDownItemForAdmin[]

  constructor(props: ScopeChangeRequestPriceBreakDownProps) {
    this.id = props.id
    this.previousTotalPrice = props.previousTotalPrice
    this.newTotalPrice = props.newTotalPrice
    this.previousTotalProPrice = props.previousTotalProPrice
    this.newTotalProPrice = props.newTotalProPrice
    this.status = props.status
    this.items = props.items
  }

  /**
   * Default value
   */
  static get none() {
    return lazyNonePriceBreakDown()
  }

  get totalPriceDiff() {
    return this.newTotalPrice - this.previousTotalPrice
  }

  get totalProPriceDiff() {
    return this.newTotalProPrice - this.previousTotalProPrice
  }

  get percentageInventoryScopeChangeItems() {
    return this.items.filter(
      (i) => i.priceType === 'Markdown' || i.priceType === 'Markup',
    )
  }

  get flatInventoryScopeChangeItems() {
    return this.items.filter(
      (i) => i.priceType === 'Flat' && !i.isBundleBreakdown,
    )
  }

  get customScopeChangeItems() {
    return this.items.filter((i) => i.priceType === 'Custom')
  }

  get bundleScopeChangeItems() {
    return this.items.filter((i) => i.isBundleBreakdown)
  }
}

/**
 * Initial values of a price breakdown row.
 */
export interface ScopeChangePriceBreakDownItemForAdminProps {
  description: string
  inventoryItemVersionId: Id
  name: string
  newPrice: number
  newProPrice: number
  newQuantity: number
  previousPrice: number
  previousProPrice: number
  previousQuantity: number
  priceType: JobScopeChangeItemPriceType
  bundleId: Id
  bundleName: string | null
  kind: JobScopeChangeItemBreakdownKind
}

/**
 * A row of a request's price breakdown: one item's change in quantity and
 * price.
 */
export class ScopeChangePriceBreakDownItemForAdmin {
  readonly description: string
  readonly inventoryItemVersionId: Id
  readonly name: string
  readonly newPrice: number
  readonly newProPrice: number
  readonly newQuantity: number
  readonly previousPrice: number
  readonly previousProPrice: number
  readonly previousQuantity: number
  readonly priceType: JobScopeChangeItemPriceType
  readonly bundleId: Id
  readonly bundleName: string | null
  readonly kind: JobScopeChangeItemBreakdownKind

  constructor(props: ScopeChangePriceBreakDownItemForAdminProps) {
    this.description = props.description
    this.inventoryItemVersionId = props.inventoryItemVersionId
    this.name = props.name
    this.newPrice = props.newPrice
    this.newProPrice = props.newProPrice
    this.newQuantity = props.newQuantity
    this.previousPrice = props.previousPrice
    this.previousProPrice = props.previousProPrice
    this.previousQuantity = props.previousQuantity
    this.priceType = props.priceType
    this.bundleId = props.bundleId
    this.bundleName = props.bundleName
    this.kind = props.kind
  }

  get priceDiff() {
    return this.newPrice - this.previousPrice
  }

  get proPriceDiff() {
    return this.newProPrice - this.previousProPrice
  }

  get quantityDiff() {
    return this.newQuantity - this.previousQuantity
  }

  /**
   * Bundle-level breakdown row. Unspecified items with a bundle id are
   * treated as bundles for older responses that predate `kind`.
   */
  get isBundleBreakdown() {
    return (
      this.kind === 'Bundle' ||
      (this.kind === 'Unspecified' && Id.some(this.bundleId))
    )
  }

  get isBundleIncludedItem() {
    return this.kind === 'BundleIncludedItem'
  }

  get displayName() {
    return this.bundleName || this.name
  }
}

const lazyNone = once(() => new ScopeChangeRequest())

const lazyNonePriceBreakDown = once(
  () =>
    new ScopeChangeRequestPriceBreakDown({
      id: Id.none,
      previousTotalPrice: 0,
      newTotalPrice: 0,
      previousTotalProPrice: 0,
      newTotalProPrice: 0,
      status: 'PendingAdminApproval',
      items: [],
    }),
)

export type JobScopeChangeItemPriceType =
  'Flat' | 'Fee' | 'Markup' | 'Markdown' | 'Custom'

export type JobScopeChangeItemBreakdownKind =
  'Unspecified' | 'ServiceItem' | 'Bundle' | 'BundleIncludedItem'
